import { POPUP_OPENED, PopupProps, PopupSize } from './types';
import { usePopupManager } from 'ui-builder';
import React, { useEffect, useRef } from 'react';
import { useEventsPublisher } from 'event-bus';
import useWindowDimensionsGetter from './windowDimensionsGetter';
import { X } from 'lucide-react';
import { cn } from 'shadcn-ui';
import { Dialog as DialogPrimitive } from 'radix-ui';

export default function ShadcnPopupLayoutWeb(props: PopupProps) {
  const popupManager = usePopupManager();
  const ref = useRef<HTMLDivElement>(null);
  const eventsPublisher = useEventsPublisher();
  const { height: windowHeight } = useWindowDimensionsGetter();

  useEffect(() => {
    if (props.popupId) {
      eventsPublisher.publish(POPUP_OPENED, {
        id: props.popupId,
      });
    }
  }, []);

  useEffect(() => {
    if (props.isBig && ref.current) {
      ref.current.style.height = `${Math.max(windowHeight - 100, 800)}px`;
    }
  }, [windowHeight]);

  return (
    <DialogPrimitive.Root
      open={true}
      onOpenChange={(open) => {
        if (!open) {
          popupManager.closePopup();
        }
      }}
    >
      <DialogPrimitive.Overlay
        className="fixed inset-0 z-[200] bg-black/50 data-[state=open]:animate-in data-[state=open]:fade-in-0"
      />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed left-[50%] translate-x-[-50%] z-[1901] w-[67rem] bg-white rounded-lg border border-gray-200 shadow-lg flex flex-col outline-none',
          props.isCentered
            ? 'top-[50%] translate-y-[-50%] max-h-[90vh]'
            : 'top-12 max-h-[calc(100vh-6rem)]',
          props.isBig && 'w-[95%] 2xl:w-[160rem]',
          props.size === PopupSize.WIDE && 'w-[80rem]',
          props.className,
        )}
        onInteractOutside={(e) => {
          const originalEvent = (e as CustomEvent<{ originalEvent: Event }>).detail.originalEvent;
          const target = originalEvent?.target as HTMLElement | null;
          if (target?.closest('.nekst-dropdown-portal')) {
            e.preventDefault();
          } else if (!props.isClosable) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          if (!props.isClosable) {
            e.preventDefault();
          }
        }}
        aria-describedby={undefined}
        data-key="popup"
        data-testid={props.testId || 'popup'}
      >
        {props.isClosable && (
          <DialogPrimitive.Close
            className="absolute top-4 right-4 rounded-sm opacity-100 hover:opacity-70 transition-opacity cursor-pointer z-10"
            data-testid="popup-close-button"
          >
            <X className="size-4 text-slate-600" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
        <div className="flex flex-col gap-2 p-6 border-b border-gray-200 shrink-0">
          <DialogPrimitive.Title className="!text-[18px] !leading-none !font-semibold font-heading uppercase text-gray-800 tracking-tight !border-b-0 !pb-0 !mb-0 !mt-0">
            {props.title}
          </DialogPrimitive.Title>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-5" data-testid="popup-content">
          {props.content}
        </div>
        {props.buttons && (
          <div className="p-6 border-t border-gray-200 shrink-0 flex justify-end gap-2">
            {props.buttons}
          </div>
        )}
      </DialogPrimitive.Content>
    </DialogPrimitive.Root>
  );
}
